import { createColorPath, isHex, normalizeHex } from './color'
import { isEmail, normalizeEmail, type Validation } from './forms'
import { isReserved } from './reserved'

/**
 * Registration plans and validation, shared by the /register page and the
 * checkout function.
 */

export type PlanId = '1' | '2' | '5' | 'lifetime'

export interface Plan {
  id: PlanId
  label: string
  /** 0 = lifetime */
  years: number
  priceCents: number
  note?: string
}

export const PLANS: Plan[] = [
  { id: '1', label: '1 Year Registration', years: 1, priceCents: 1995 },
  { id: '2', label: '2 Year Registration', years: 2, priceCents: 2995, note: 'Save 25%' },
  { id: '5', label: '5 Year Registration', years: 5, priceCents: 4995, note: 'Save 50%' },
  { id: 'lifetime', label: 'Lifetime Registration', years: 0, priceCents: 9995, note: 'Best Value' },
]

export function planById(id: unknown): Plan | undefined {
  return PLANS.find((p) => p.id === id)
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

export const NAME_MIN = 6
export const NAME_MAX = 50
const NAME_MAX_WORDS = 7
// Same list the seed script uses to clean the public names.
const PROFANE = /\b(fuck|shit|cunt|nigg|fag|retard|rape|nazi|hitler)\w*/i

export type NameCheck = { ok: true; name: string; slug: string } | { ok: false; error: string }

/** Letters and spaces, at least 6 letters, a slug that is not reserved. */
export function validateColorName(raw: string): NameCheck {
  const name = raw.trim().replace(/\s+/g, ' ')
  if (!name) return { ok: false, error: 'Please name your color.' }
  if (!/^[a-zA-Z ]+$/.test(name)) return { ok: false, error: 'Names can only use letters and spaces.' }
  if (name.replaceAll(' ', '').length < NAME_MIN) return { ok: false, error: `Names must be at least ${NAME_MIN} letters.` }
  if (name.length > NAME_MAX) return { ok: false, error: `Names must be ${NAME_MAX} characters or fewer.` }
  if (name.split(' ').length > NAME_MAX_WORDS) return { ok: false, error: `Names can have up to ${NAME_MAX_WORDS} words.` }
  if (PROFANE.test(name)) return { ok: false, error: 'That name is not allowed.' }
  const slug = createColorPath(name)
  if (isReserved(slug)) return { ok: false, error: 'That name is reserved.' }
  return { ok: true, name, slug }
}

export function validateHex(raw: string): { ok: true; hex: string } | { ok: false; error: string } {
  const v = raw.trim()
  if (!v) return { ok: false, error: 'Pick a color.' }
  if (!isHex(v)) return { ok: false, error: 'Color codes are six hex digits, like #1e90ff.' }
  return { ok: true, hex: normalizeHex(v) }
}

/** Expiration = registration date + N years; lifetime has none. */
export function expirationDate(registered: Date, years: number): string | null {
  if (years <= 0) return null
  const d = new Date(Date.UTC(registered.getUTCFullYear() + years, registered.getUTCMonth(), registered.getUTCDate()))
  return d.toISOString().slice(0, 10)
}

export interface RegistrationInput {
  hex: string
  name: string
  slug: string
  plan: Plan
  firstName: string
  lastName: string
  email: string
}

export type RegistrationField = 'hex' | 'name' | 'plan' | 'firstName' | 'lastName' | 'email'
export type RegistrationErrors = Partial<Record<RegistrationField, string>>

export function validateRegistration(input: Record<string, unknown>): Validation<RegistrationInput> {
  const errors: RegistrationErrors = {}
  const str = (k: string, max: number) => (typeof input[k] === 'string' ? (input[k] as string).trim().slice(0, max) : '')

  const hex = validateHex(str('hex', 7))
  if (!hex.ok) errors.hex = hex.error
  const name = validateColorName(str('name', NAME_MAX + 20))
  if (!name.ok) errors.name = name.error
  const plan = planById(input.plan)
  if (!plan) errors.plan = 'Choose a registration type.'
  const firstName = str('firstName', 80)
  const lastName = str('lastName', 80)
  const email = normalizeEmail(input.email)
  if (!firstName) errors.firstName = 'Please enter your first name.'
  if (!lastName) errors.lastName = 'Please enter your last name.'
  if (!isEmail(email)) errors.email = 'Please enter a valid email address.'

  if (!hex.ok || !name.ok || !plan || Object.keys(errors).length) return { ok: false, errors }
  return { ok: true, value: { hex: hex.hex, name: name.name, slug: name.slug, plan, firstName, lastName, email } }
}
